import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[10001] bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * Iter-98: Google Picker (and any third-party portal that injects DOM at the
 * <body> root) triggers Radix's "interact-outside" detection and silently closes
 * the parent Dialog while the user is interacting with the Picker. The fix is
 * to simply ignore EVERY Radix outside-interaction event while a Picker DOM is
 * mounted — much more robust than parsing event targets (which doesn't catch
 * iframe focus-trap escapes). Add new selectors here as we integrate more 3P
 * pickers.
 */
const THIRD_PARTY_PORTAL_SELECTOR = [
  ".picker-dialog",
  ".picker-dialog-bg",
  ".picker-dialog-content",
  ".picker-frame",
  ".picker",
  '[class*="picker-"]',          // Google Picker dynamic class names
  "iframe[src*=\"docs.google.com/picker\"]",
].join(",");

function isThirdPartyPortalOpen() {
  if (typeof document === "undefined") return false;
  return !!document.querySelector(THIRD_PARTY_PORTAL_SELECTOR);
}

const DialogContent = React.forwardRef(({ className, children, onPointerDownOutside, onInteractOutside, onFocusOutside, onEscapeKeyDown, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      onPointerDownOutside={(e) => {
        if (isThirdPartyPortalOpen()) { e.preventDefault(); return; }
        onPointerDownOutside?.(e);
      }}
      onFocusOutside={(e) => {
        if (isThirdPartyPortalOpen()) { e.preventDefault(); return; }
        onFocusOutside?.(e);
      }}
      onInteractOutside={(e) => {
        if (isThirdPartyPortalOpen()) { e.preventDefault(); return; }
        onInteractOutside?.(e);
      }}
      onEscapeKeyDown={(e) => {
        if (isThirdPartyPortalOpen()) { e.preventDefault(); return; }
        onEscapeKeyDown?.(e);
      }}
      className={cn(
        // Mobile-first: width respects 1rem gutters on each side; content scrolls
        // vertically if it exceeds 90vh. On sm+ desktop we revert to the original
        // capped width (max-w-lg) and the comfortable 1.5rem padding.
        "fixed left-[50%] top-[50%] z-[10002] grid w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-4 sm:p-6 shadow-lg duration-200",
        "max-h-[90vh] overflow-y-auto overscroll-contain",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] rounded-lg",
        className
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
